import type {
    ActionResponse,
    Department,
    FinishedActionResponse,
    FinishedSaveResponse,
    HierarchyTree,
    Record,
    RecordDescriptor,
    RecordHierarchyTreeNode,
    RecordHolding,
    RichDocument,
    RichRecord,
    UUID,
    Valuable
} from 'typings/portaro.be.types';
import type {IHttpService} from 'angular';
import {
    responseToData,
    transferify,
    unRestangularize
} from 'shared/utils/data-service-utils';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {AnyObject} from 'typings/portaro.fe.types';
import {RecordCopyRequest} from './record-copy.request';
import {RecordMergeRequest} from './record-merge.request';

/**
 * @ngdoc service
 * @name RecordDataService
 * @module uc.record
 *
 * @description
 * Service for `Record` handling
 *
 * @requires service:$http
 */
export default class RecordDataService {
    public static readonly serviceName = 'recordDataService';
    public static readonly RECORD_ROUTE = 'records';
    public static readonly RECORD_HOLDINGS_ROUTE = 'record-holdings';
    public static readonly RELATED_RECORDS_ROUTE = 'related-records-count';
    public static readonly PARTS_ROUTE = 'parts';
    public static readonly ARTICLES_ROUTE = 'articles';
    public static readonly HIERARCHIES_ROUTE = 'hierarchies';
    public static readonly HIERARCHY_ROOT_ROUTE = 'root';

    /*@ngInject*/
    constructor(private $http: IHttpService,
                private Restangular: Restangular.IService,
                private CONTEXT_PATH: string) {
    }

    @unRestangularize()
    public async getById(recordId: string): Promise<RichRecord> {
        try {
            return await this.Restangular
                .one(RecordDataService.RECORD_ROUTE, recordId)
                .get();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @unRestangularize()
    public async createRecordHolding(department: Department, record?: Record): Promise<FinishedSaveResponse<RecordHolding>> {
        try {
            return await this.Restangular
                .one(RecordDataService.RECORD_HOLDINGS_ROUTE)
                .post('', transferify({department, record}));
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    public async mergeRecords(target: Record, sources: Record[], mergeDetail?: boolean): Promise<ActionResponse> {
        try {
            const data = transferify(new RecordMergeRequest(target, sources, mergeDetail));
            const httpResponse = await this.$http.post<ActionResponse>(`${this.CONTEXT_PATH}/api/records/merge`, data);
            return responseToData(httpResponse);
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    public async copyRecord(source: Record): Promise<Record> {
        try {
            const data = transferify(new RecordCopyRequest(source, null));
            const httpResponse = await this.$http.post<FinishedSaveResponse<Record>>(`${this.CONTEXT_PATH}/api/records/copy`, data);
            return responseToData(httpResponse).savedObject;
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @unRestangularize()
    public async queryRecordHoldings(params: AnyObject): Promise<RecordHolding[]> {
        try {
            return await this.Restangular
                .all(RecordDataService.RECORD_HOLDINGS_ROUTE)
                .getList(transferify(params, false));
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @unRestangularize()
    public async deleteRecordHolding(recordHolding: RecordHolding): Promise<FinishedActionResponse> {
        try {
            return await this.Restangular
                .one(RecordDataService.RECORD_HOLDINGS_ROUTE)
                .one('delete')
                .post('', transferify({recordHolding}));
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @unRestangularize()
    public async getRecordParts(recordId: string, pageNumber: number, pageSize: number): Promise<RichDocument[]> {
        try {
            return await this.Restangular
                .one(RecordDataService.RECORD_ROUTE, recordId)
                .all(RecordDataService.PARTS_ROUTE)
                .getList({pageNumber, pageSize});
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @unRestangularize()
    public async getRecordArticles(recordId: string, pageNumber: number, pageSize: number): Promise<RichDocument[]> {
        try {
            return await this.Restangular
                .one(RecordDataService.RECORD_ROUTE, recordId)
                .all(RecordDataService.ARTICLES_ROUTE)
                .getList({pageNumber, pageSize});
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async getRelatedRecordsCount(recordId: string): Promise<Valuable<number>> {
        try {
            return await this.Restangular
                .one(RecordDataService.RECORD_ROUTE, recordId)
                .one(RecordDataService.RELATED_RECORDS_ROUTE)
                .get();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @unRestangularize()
    public async getRecordHierarchyTrees(recordId: string): Promise<HierarchyTree<UUID, RecordHierarchyTreeNode>[]> {
        try {
            return await this.Restangular
                .one(RecordDataService.RECORD_ROUTE, recordId)
                .all(RecordDataService.HIERARCHIES_ROUTE)
                .getList();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @unRestangularize()
    public async getHierarchyRoot(): Promise<RecordDescriptor> {
        try {
            return await this.Restangular
                .one(RecordDataService.RECORD_ROUTE, RecordDataService.HIERARCHY_ROOT_ROUTE)
                .get();
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

    @ngAsync()
    @unRestangularize()
    public async query(filterParam: AnyObject): Promise<RecordDescriptor[]> {
        try {
            return await this.Restangular
                .all(RecordDataService.RECORD_ROUTE)
                .getList(filterParam);
        } catch (httpError) {
            throw responseToData(httpError);
        }
    }

}
